import React from 'react';
import { ARROW_CONFIG } from '../../config/onboarding';

/**
 * 🎓 LeaderArrow — bouncing onboarding arrow rendered above a leader head.
 *
 * Renders only during turns 1-2 on the player's turn. The parent
 * (MapView2D) handles visibility logic; this is a pure presentational
 * component.
 *
 * @param {string} relationship - 'self' | 'strong_ally' | 'semi_ally' | 'enemy'
 * @param {'top'|'right'} placement - Where the arrow sits relative to the leader.
 *        'top' = above the head (default).
 *        'right' = to the right side at 3 o'clock — used for China whose
 *        leader sprite renders so far north the top arrow gets cropped.
 */
export default function LeaderArrow({ relationship, placement = 'top' }) {
  const cfg = ARROW_CONFIG[relationship];
  if (!cfg) return null;

  if (placement === 'right') {
    // Pill sits at 3 o'clock relative to the leader's face. The leader
    // wrapper is 180×180 but the actual visible leader sprite is centered
    // and ~120px wide, so the pill sits at 60-ish % from left of the
    // wrapper — right next to the leader's shoulder, always visible
    // whenever the leader itself is visible.
    return (
      <div
        className="absolute pointer-events-none select-none flex items-center animate-pulse-soft"
        style={{
          top: '28%',
          left: '62%',
          zIndex: 25,
        }}
        data-testid={`onboarding-arrow-${relationship}`}
      >
        {/* Triangle pointing left at the leader's face */}
        <div
          style={{
            width: 0,
            height: 0,
            borderTop: '7px solid transparent',
            borderBottom: '7px solid transparent',
            borderRight: `9px solid ${cfg.color}`,
            filter: `drop-shadow(0 0 6px ${cfg.glow})`,
          }}
        />
        {/* Label pill */}
        <div
          className="px-2 py-1 rounded-md whitespace-nowrap font-bold tracking-wider"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            border: `2px solid ${cfg.color}`,
            color: cfg.color,
            boxShadow: `0 0 14px ${cfg.glow}, 0 2px 6px rgba(0,0,0,0.6)`,
            fontSize: '10px',
            fontFamily: "'JetBrains Mono', monospace",
            marginLeft: '-1px',
          }}
        >
          <span style={{ marginRight: '4px' }}>{cfg.icon}</span>
          {cfg.label}
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute left-1/2 pointer-events-none select-none animate-bounce-soft"
      style={{
        top: '-58px',
        transform: 'translateX(-50%)',
        zIndex: 25,
      }}
      data-testid={`onboarding-arrow-${relationship}`}
    >
      {/* Label pill */}
      <div
        className="px-2 py-1 rounded-md whitespace-nowrap font-bold tracking-wider"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          border: `2px solid ${cfg.color}`,
          color: cfg.color,
          boxShadow: `0 0 14px ${cfg.glow}, 0 2px 6px rgba(0,0,0,0.6)`,
          fontSize: '10px',
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        <span style={{ marginRight: '4px' }}>{cfg.icon}</span>
        {cfg.label}
      </div>

      {/* Triangle pointing down at the leader */}
      <div
        className="mx-auto"
        style={{
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: `10px solid ${cfg.color}`,
          marginTop: '-1px',
          filter: `drop-shadow(0 0 6px ${cfg.glow})`,
        }}
      />
    </div>
  );
}
